import { Interpolation, Theme } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { Icon, IconColorType, IconName } from '@frontend/icons';
import { InboxPrefixes } from '@frontend/tracking-prefixes';
import { theme } from '@frontend/theme';
import { Button, PopoverMenu, PopoverMenuItem, Text } from '@frontend/design-system';
import { AIReceptionistFilterStatus } from '../../../types';
import { FilterItemPopoverCheckboxField } from '../filter-item-popover-checkbox-field';
import { FilterItemPopoverRadioField } from '../filter-item-popover-radio';
import { GetItemPropsFn, GetMenuPropsFn } from '../types';

type AiReceptionistFilterPopoverMenuProps = {
  getMenuProps: GetMenuPropsFn;
  getItemProps: GetItemPropsFn;
  groupIds?: string[];
  toggleItems: (selectedAiStatuses: string[]) => void;
  selectedAiStatuses: string[];
};

export const AiReceptionistFilterPopoverMenu = ({
  getMenuProps,
  getItemProps,
  toggleItems,
  selectedAiStatuses,
}: AiReceptionistFilterPopoverMenuProps): ReturnType<typeof PopoverMenu> => {
  const { t } = useTranslation('inbox');

  const availableAiStatuses = Object.values(AIReceptionistFilterStatus);

  const botStatuses = availableAiStatuses.slice(0, 2);
  const botOutcomes = availableAiStatuses.slice(2);

  const statusLabelMap: Record<string, string> = {
    [AIReceptionistFilterStatus.IS_ACTIVE]: t('Active'),
    [AIReceptionistFilterStatus.IS_INACTIVE]: t('Not Active'),
    [AIReceptionistFilterStatus.NEEDS_ATTENTION]: t('Needs Attention'),
    [AIReceptionistFilterStatus.ADDED_TO_SCHEDULE]: t('Added to Schedule'),
    [AIReceptionistFilterStatus.APPOINTMENT_REQUESTED]: t('Appointment Requested'),
    [AIReceptionistFilterStatus.CANCELED]: t('Cancelled'),
    [AIReceptionistFilterStatus.COMPLETE]: t('Complete'),
  };

  const statusIconMap: Record<string, IconName> = {
    [AIReceptionistFilterStatus.IS_ACTIVE]: 'aiweave-assistant',
    [AIReceptionistFilterStatus.IS_INACTIVE]: 'aireceptionist-inactive',
    [AIReceptionistFilterStatus.NEEDS_ATTENTION]: 'aineeds-attention',
    [AIReceptionistFilterStatus.ADDED_TO_SCHEDULE]: 'calendar-scheduled',
    [AIReceptionistFilterStatus.APPOINTMENT_REQUESTED]: 'calendar-requested',
    [AIReceptionistFilterStatus.CANCELED]: 'calendar-canceled',
    [AIReceptionistFilterStatus.COMPLETE]: 'check',
  };

  const statusIconColorMap: Record<string, IconColorType> = {
    [AIReceptionistFilterStatus.IS_ACTIVE]: 'default',
    [AIReceptionistFilterStatus.IS_INACTIVE]: 'subdued',
    [AIReceptionistFilterStatus.NEEDS_ATTENTION]: 'default',
    [AIReceptionistFilterStatus.ADDED_TO_SCHEDULE]: 'default',
    [AIReceptionistFilterStatus.APPOINTMENT_REQUESTED]: 'default',
    [AIReceptionistFilterStatus.CANCELED]: 'error',
    [AIReceptionistFilterStatus.COMPLETE]: 'success',
  };

  // need to SerializedStyles to use correct css object type
  const statusIconCssMap: Record<string, Interpolation<Theme> | undefined> = {
    [AIReceptionistFilterStatus.IS_ACTIVE]: undefined,
    [AIReceptionistFilterStatus.IS_INACTIVE]: undefined,
    [AIReceptionistFilterStatus.NEEDS_ATTENTION]: needsAttentionStyles,
    [AIReceptionistFilterStatus.ADDED_TO_SCHEDULE]: addedToScheduleStyles,
    [AIReceptionistFilterStatus.APPOINTMENT_REQUESTED]: appointmentRequestedStyles,
    [AIReceptionistFilterStatus.CANCELED]: canceledStyles,
    [AIReceptionistFilterStatus.COMPLETE]: undefined,
  };

  const hasAllSelected = availableAiStatuses.every((status) => selectedAiStatuses.includes(status));
  const hasNoneSelected = selectedAiStatuses.length === 0;

  const handleSelectAll = () => {
    const unselectedStatuses = botOutcomes.filter((status) => !selectedAiStatuses.includes(status));
    toggleItems(unselectedStatuses);
  };

  const handleClear = () => {
    toggleItems(selectedAiStatuses);
  };

  const handleToggleActiveStatus = (aiStatus: string) => {
    toggleItems([aiStatus]);

    if (
      aiStatus === AIReceptionistFilterStatus.IS_INACTIVE &&
      selectedAiStatuses.includes(AIReceptionistFilterStatus.IS_ACTIVE)
    ) {
      toggleItems([AIReceptionistFilterStatus.IS_ACTIVE]);
    }
    if (
      aiStatus === AIReceptionistFilterStatus.IS_ACTIVE &&
      selectedAiStatuses.includes(AIReceptionistFilterStatus.IS_INACTIVE)
    ) {
      toggleItems([AIReceptionistFilterStatus.IS_INACTIVE]);
    }
  };

  return (
    <PopoverMenu {...getMenuProps()} css={{ minWidth: 180 }}>
      <PopoverMenuItem
        as='span'
        {...getItemProps({
          index: 0,
          disableCloseOnSelect: true,
        })}
        css={{
          marginTop: theme.spacing(1),
          padding: theme.spacing(1, 0.75),
          borderBottom: `1px solid ${theme.colors.neutral20}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          ':hover': {
            backgroundColor: 'inherit',
          },
          ':focus': {
            backgroundColor: 'inherit',
          },
        }}
      >
        <Button
          variant='tertiary'
          css={{
            color: theme.font.colors.primary,
            fontWeight: theme.font.weight.semibold,
          }}
          onClick={handleSelectAll}
          disabled={hasAllSelected}
          trackingId={`${InboxPrefixes.List}-filters-popover-ai-receptionist-select-all`}
        >
          {t('Select All')}
        </Button>
        <Button
          variant='tertiary'
          css={{
            color: theme.font.colors.primary,
            fontWeight: theme.font.weight.semibold,
          }}
          onClick={handleClear}
          disabled={hasNoneSelected}
          trackingId={`${InboxPrefixes.List}-filters-popover-ai-receptionist-clear`}
        >
          {t('Clear')}
        </Button>
      </PopoverMenuItem>
      <div
        css={{
          maxHeight: 320,
          overflowY: 'auto',
        }}
      >
        {botStatuses.map((aiStatus, index) => (
          <PopoverMenuItem
            key={aiStatus}
            {...getItemProps({
              index: index + 1,
              disableCloseOnSelect: true,
            })}
            css={{
              width: '100%',
              cursor: 'pointer',
              borderBottom: index === 1 ? `1px solid ${theme.colors.neutral20}` : 'none',
              '*': {
                cursor: 'pointer',
              },
            }}
          >
            <FilterItemPopoverRadioField
              label=''
              options={[aiStatus]}
              onChange={(value) => {
                handleToggleActiveStatus(value);
              }}
              value={selectedAiStatuses.includes(aiStatus) ? aiStatus : undefined}
              displayValue={
                <div
                  css={{
                    display: 'flex',
                    alignItems: 'center',
                    columnGap: theme.spacing(1),
                  }}
                >
                  <Icon name={statusIconMap[aiStatus]} size={16} color={statusIconColorMap[aiStatus]} />
                  <Text>{statusLabelMap[aiStatus]}</Text>
                </div>
              }
            />
          </PopoverMenuItem>
        ))}
        {botOutcomes.map((aiStatus, index) => (
          <PopoverMenuItem
            key={aiStatus}
            {...getItemProps({
              index: index + 1,
              disableCloseOnSelect: true,
              onClickCapture: (e) => {
                e.preventDefault();
                e.stopPropagation();
                toggleItems([aiStatus]);
              },
            })}
            css={{
              width: '100%',
              cursor: 'pointer',
              '*': {
                cursor: 'pointer',
              },
            }}
          >
            <FilterItemPopoverCheckboxField
              isChecked={selectedAiStatuses.includes(aiStatus)}
              name={`ai-receptionist-${aiStatus}`}
              label={
                <div
                  css={{
                    display: 'flex',
                    alignItems: 'center',
                    columnGap: theme.spacing(1),
                  }}
                >
                  <Icon
                    name={statusIconMap[aiStatus]}
                    size={16}
                    color={statusIconColorMap[aiStatus]}
                    css={statusIconCssMap[aiStatus]}
                  />
                  <Text>{statusLabelMap[aiStatus]}</Text>
                </div>
              }
            />
          </PopoverMenuItem>
        ))}
      </div>
    </PopoverMenu>
  );
};

const needsAttentionStyles = {
  borderRadius: theme.borderRadius.small,
  background: `linear-gradient(100deg, ${theme.colors.warning10} 0%, ${theme.colors.warning5} 100%)`,
  color: theme.colors.warning80,
};

const addedToScheduleStyles = {
  color: theme.colorsV2.blue50(),
};

const appointmentRequestedStyles = {
  color: 'var(--fabric-color-seaweed-50)',
};

const canceledStyles = {
  color: theme.colorsV2.tangerine50(),
};
